package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.strings.EditorStrings

/**
 * First-run vs. regeneration state machine for the editor's success copy (mustFix 1 -- see the
 * design spec's "ERFOLGS-COPY, ERSTLAUF vs. REGENERIERUNG"). Pure, DOM-free logic so the exact
 * wording rule is directly unit-testable (see `GenerateStateTest`) instead of only verifiable by
 * eye in a browser.
 *
 * The editor tracks a single `diagramExistedBefore: Boolean` flag per project, seeded from the
 * initial `GET /render` (`200` -> `true`, `204` -> `false`) and updated via [afterSuccess] /
 * [afterValidationRejected] after every subsequent call.
 *
 * R-18: [successMessage] takes the active language's [EditorStrings] as a parameter rather than
 * reading a fixed German constant -- kept a pure function of its inputs (see this wave's plan
 * §5.7) so both languages stay independently unit-testable.
 */
public object GenerateState {
    /** The status-area success copy for a `200` response to `POST .../ai/generate`. Never
     * "updated" the very first time a project gets a diagram -- that word implies a "before" that
     * never existed. */
    public fun successMessage(
        diagramExistedBefore: Boolean,
        strings: EditorStrings,
    ): String = if (diagramExistedBefore) strings.diagramUpdated else strings.diagramCreated

    /** After ANY successful generation (first-run or not), a diagram is now known to exist -- the
     * next successful call, if any, is always a regeneration. */
    public fun afterSuccess(diagramExistedBefore: Boolean): Boolean = true

    /** A `422` (validation rejection) never changes whether a diagram is known to exist -- the
     * design spec is explicit that this must not "spend" the first-run moment: the next successful
     * call after a 422 still reports the "created" copy if none existed before. */
    public fun afterValidationRejected(diagramExistedBefore: Boolean): Boolean = diagramExistedBefore
}
